import React from "react";

import { formatDistanceToNow } from "date-fns";

import {
  Close as CloseIcon,
  CheckCircle,
  Error as ErrorIcon,
  Launch as LaunchIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  MoreHoriz as LoadingIcon,
  AccessTime,
  Code as CodeIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
  Stack,
  Chip,
  List,
  ListItem,
  ListItemText,
  Link,
  ListItemIcon,
  Paper,
  Grid,
} from "@mui/material";

import { componentStyles, borderRadius } from "../styles/themeStyles";
import { ClusterApp, ClusterStatus } from "../types/cluster";

interface AppDetailsProps {
  app: ClusterApp;
  cluster: ClusterStatus;
  open: boolean;
  onClose: () => void;
}

const AppDetails = ({ app, cluster, open, onClose }: AppDetailsProps) => {
  // Get namespace and app name
  const [namespace = "", appName = ""] = app.name.split("/");

  // Get app status
  const getStatusChip = () => {
    if (app.status.final && !app.status.error) {
      return (
        <Chip
          icon={<CheckCircle />}
          label="Ready"
          color="success"
          sx={{ fontWeight: 'medium' }}
        />
      );
    } else if (app.status.error) {
      return (
        <Chip
          icon={<ErrorIcon />}
          label="Error"
          color="error"
          sx={{ fontWeight: 'medium' }}
        />
      );
    } else {
      return (
        <Chip
          icon={<AccessTime />}
          label="Deploying"
          color="warning"
          sx={{ fontWeight: 'medium' }}
        />
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: borderRadius.default,
          backgroundColor: "background.paper",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6" component="span">
            Application Details
          </Typography>
          {getStatusChip()}
        </Stack>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: 3, py: 3 }}>
        <Grid container spacing={3}>
          {/* Basic Info */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ ...componentStyles.card, p: 3, mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                Basic Information
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {appName || app.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Namespace
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {namespace}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Cluster
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {cluster.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Status Message
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {app.status.message || "No message"}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>

          {/* Endpoints */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{ ...componentStyles.card, p: 3, height: "100%" }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <LinkIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Endpoints
                </Typography>
              </Stack>
              {app.status.hrefs && app.status.hrefs.length > 0 ? (
                <List sx={{ p: 0 }}>
                  {app.status.hrefs.map((link, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        px: 2,
                        py: 1,
                        borderRadius: 1,
                        "&:hover": { bgcolor: "action.hover" },
                        mb: 1,
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <LaunchIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Link
                            href={link}
                            target="_blank"
                            rel="noopener"
                            sx={{
                              color: "primary.main",
                              textDecoration: "none",
                              "&:hover": {
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {new URL(link).hostname}
                          </Link>
                        }
                        secondary={link}
                        secondaryTypographyProps={{
                          sx: {
                            fontSize: "0.75rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <InfoIcon color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                  <Typography color="text.secondary">
                    No endpoints available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Status & Diagnostics */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{ ...componentStyles.card, p: 3, height: "100%" }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <CodeIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Diagnostics
                </Typography>
              </Stack>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Deployment Status
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 500,
                      color: app.status.error
                        ? "error.main"
                        : app.status.final
                          ? "success.main"
                          : "warning.main",
                    }}
                  >
                    {app.status.error
                      ? "Failed"
                      : app.status.final
                        ? "Completed"
                        : "In Progress"}
                  </Typography>
                </Box>

                {app.status.error && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Error Details
                    </Typography>
                    <Typography
                      variant="body2"
                      color="error.main"
                      sx={{
                        p: 1.5,
                        bgcolor: "error.light",
                        borderRadius: 1,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {app.status.error}
                    </Typography>
                  </Box>
                )}

                {!app.status.final && !app.status.error && (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <LoadingIcon sx={{ color: "warning.main", mr: 1 }} />
                    <Typography color="text.secondary">
                      Deployment in progress...
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default AppDetails;